import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { blogApi } from "../lib/blogApi";
import { Snowflake, ArrowLeft, Clock, Calendar, ArrowRight, Tag } from "lucide-react";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const PostCard = ({ post, featured = false }) => (
  <Link
    to={`/blog/${post.slug}`}
    data-testid={`blog-card-${post.slug}`}
    className={`group block bg-white border border-slate-100 rounded-2xl overflow-hidden hover:border-sky-200 hover:shadow-xl hover:shadow-sky-100/40 transition-all duration-500 ${
      featured ? "md:col-span-2" : ""
    }`}
  >
    <div className={`relative overflow-hidden ${featured ? "aspect-[2/1]" : "aspect-[4/3]"} bg-slate-100`}>
      {post.cover_image && (
        <img
          src={post.cover_image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      )}
      {post.category && (
        <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-sky-700 shadow-sm">
          {post.category}
        </span>
      )}
    </div>
    <div className="p-6">
      <h3 className={`font-bold text-slate-900 leading-snug mb-2 group-hover:text-sky-600 transition-colors ${
        featured ? "text-2xl" : "text-lg"
      }`}>
        {post.title}
      </h3>
      {post.subtitle && (
        <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2">{post.subtitle}</p>
      )}
      <div className="flex items-center gap-4 text-xs text-slate-400 mt-4 pt-4 border-t border-slate-100">
        <span className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {post.read_minutes} min read
        </span>
        <span className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(post.created_at)}
        </span>
        <span className="ml-auto flex items-center gap-1 text-sky-500 font-medium group-hover:gap-2 transition-all">
          Read
          <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  </Link>
);

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("All");

  useEffect(() => {
    blogApi
      .list()
      .then((res) => setPosts(res.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(posts.map((p) => p.category).filter(Boolean)))];
  const filtered = categoryFilter === "All" ? posts : posts.filter((p) => p.category === categoryFilter);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30 backdrop-blur-md bg-white/90">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-cyan-300 flex items-center justify-center">
              <Snowflake className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-900">Frostyware 3D</span>
          </Link>
          <Link
            to="/"
            data-testid="blog-back-home"
            className="text-sm text-slate-500 hover:text-sky-600 flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold uppercase tracking-wider mb-4">
            <Tag className="w-3.5 h-3.5" />
            The Frostyware Journal
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
            Notes on 3D printing, modeling, and the craft of making things.
          </h1>
          <p className="text-base text-slate-500 mt-6 leading-relaxed max-w-2xl">
            Honest write-ups from inside the workshop — Blender, ZBrush, VFX, lithophanes, and
            everything we've learned the hard way so you don't have to.
          </p>
        </div>
      </section>

      {/* Category filter */}
      {!loading && categories.length > 1 && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategoryFilter(c)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  categoryFilter === c
                    ? "bg-slate-900 text-white shadow"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading posts…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400">No posts yet in this category.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {featured && <PostCard post={featured} featured />}
            {rest.map((p) => (
              <PostCard key={p.id} post={p} />
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-slate-100 bg-white py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-400">
          <span>© {new Date().getFullYear()} Frostyware 3D. All rights reserved.</span>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-sky-500 transition-colors">Privacy Policy</Link>
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
